<template>
  <div class="expandable-settings-item gw-planning" :class="{ expand: planningExpanded }">
    <div class="expandable-settings-item__title v-centerer" @click="planningExpanded = !planningExpanded">
      <Icon :rotate-left="!planningExpanded">{{ arrowDownLine }}</Icon>
      <div class="gw-planning__title">
        {{ title }}
      </div>
      <div class="generic-border generic-border--icon-button generic-border--red gw-planning__delete">
        <Icon @click.native.stop="deletePlanning">
          {{ deleteMedium }}
        </Icon>
      </div>
    </div>
    <TransitionExpand>
      <div v-show="planningExpanded" class="sd-overflow sd-overflow-visible sd-overflow--auto-height">
        <EditableLabelRadio
          v-model="planningCopy.participants"
          class="mt5 participants"
          separator=", "
          :legend="$t('message.GW_PARTICIPANTS') + $t('message.TWO_DOTS')"
          :items="selectableUsers"
          multiple
          :disabled="currentTeam.id !== planningCopy.team"
          @input="edit('participants')"
        />
        <EventPlanning
          ref="evtPlanning"
          v-model="planningCopy"
          :disabled="isDisabled"
          @input="edit(['repeatType', 'repeatData'])"
        />
      </div>
    </TransitionExpand>
  </div>
</template>

<script>
/**
 * Component used to setup a planning of a GW template.
 * Trigger edition XHRs from the component directly.
 */

import { cloneDeep } from 'lodash';
// Vuex
import { mapState } from 'vuex';
import { FETCH_TEAMS } from '@/store/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
import { SET_GW_PLANNING } from '@/store/modules/gemba-walks/action-types';
// Components
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import EventPlanning from '@/components/event-planning.vue';
import Icon from '@/components/icon.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Utils
import { calert, decodeHTML } from '@/utils/main';
import { arrowDownLine, deleteMedium } from '@/assets/icons';

export default {

  name: 'GwPlanning',

  components: {
    EditableLabelRadio,
    EventPlanning,
    Icon,
    TransitionExpand,
  },

  props: {
    planning: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      arrowDownLine,
      deleteMedium,
      mounted: false,
      planningExpanded: false,
      ongoingEdit: false,
      planningCopy: {},
    };
  },

  computed: {
    /** Boolean: Whether this planning should be disbaled or not */
    isDisabled() {
      return this.currentTeam.id !== this.planningCopy.team;
    },
    title() {
      if (this.mounted) {
        const choices = this.$refs.evtPlanning.repeatChoices;
        const { repeatType, teamName } = this.planningCopy;
        return `${choices[repeatType].label}${this.$t('message.TWO_DOTS')}${decodeHTML(teamName)}`;
      }
      return '';
    },
    selectableUsers() {
      return this.teamsData.map(team => ({
        title: team.name,
        radioItems: team.users.map(user => ({
          id: user.id,
          label: this.$t('fullNameFormat', user),
        })),
      }));
    },
    ...mapState({
      teamsData: state => state[GET_TEAMS.stateKey] || [],
      teamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
  },

  watch: {
    planning: {
      deep: true,
      handler() {
        if (!this.ongoingEdit) {
          this.planningCopy = Object.assign({}, this.planning);
        }
      },
    },
  },

  created() {
    this.planningCopy = cloneDeep(this.planning);
  },

  mounted() {
    this.mounted = true;
    if (!this.teamsLoading) {
      this.$store.dispatch(FETCH_TEAMS);
    }
  },

  methods: {
    /** Trigger the edition XHR of the GW planning */
    edit(paramKeys) {
      if (this.isDisabled) {
        return;
      }
      paramKeys = (paramKeys instanceof Array) ? paramKeys : [paramKeys];
      const params = { id: this.planningCopy.id, gw: this.planningCopy.gw };
      paramKeys.forEach((key) => {
        params[key] = this.planningCopy[key];
      });
      this.ongoingEdit = true;
      this.$store.dispatch(`gembaWalks/${SET_GW_PLANNING}`, params)
        .then(() => { this.ongoingEdit = false; })
        .catch(() => { this.ongoingEdit = false; });
    },
    /** Prompt confirmation to delete the current planning */
    deletePlanning() {
      calert(this.$t('message.GW_CONFIRM_DELETE_PLANNING'), [
        {
          title: this.$t('message.DELETE'),
          sClass: 'red',
          action: () => {
            this.$emit('delete');
          },
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-planning {

  &.expandable-settings-item {
    margin-bottom: 0px;

    &:not(:last-child) {
      margin-bottom: 0.7rem;
    }

    .expandable-settings-item__title {
      padding: 0.5rem 0.5rem 0.5rem 1rem;

      > .icon {
        margin-right: .6rem;
      }
    }
  }


  &__title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    min-width: 0px;
    flex: 1;
  }

  &__delete {
    margin-left: .4rem;

    &.generic-border--icon-button {
      width: 2.5rem;
      height: 2.5rem;

      .icon {
        margin: 0px;
        margin-right: 0px;
      }
    }
  }
}
</style>
